import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import type { ReactNode } from 'react'
import { DrawerMobileFullBleedGallery } from './TripPlanDetailDrawer.styles'

export const DRAWER_DETAIL_MOBILE_GALLERY_PREVIEW_COUNT = 1

interface DrawerDetailHeroPlacement {
  leadingHero: ReactNode
  inlineHero: ReactNode
}

export function DrawerDetailHeroLoading({
  mobileFullWidth = false,
}: {
  mobileFullWidth?: boolean
}) {
  return (
    <LoadingBox
      height={280}
      borderRadius={mobileFullWidth ? 'none' : 'L'}
      style={{ width: '100%' }}
    />
  )
}

/**
 * Splits a drawer hero gallery between modal (leading, full-bleed) and
 * right-panel (inline, padded) presentation.
 */
export function splitDrawerDetailHero(
  mobileFullWidth: boolean,
  hero: ReactNode | null | undefined,
): DrawerDetailHeroPlacement {
  if (!hero) {
    return { leadingHero: null, inlineHero: null }
  }

  if (mobileFullWidth) {
    return {
      leadingHero: (
        <DrawerMobileFullBleedGallery data-testid="drawer-detail-mobile-hero">
          {hero}
        </DrawerMobileFullBleedGallery>
      ),
      inlineHero: null,
    }
  }

  return { leadingHero: null, inlineHero: hero }
}

/**
 * Breaks a hero gallery out of a padded content wrapper on mobile modal.
 * Use when the surrounding layout keeps the hero inside ContentWrapper.
 */
export function wrapDrawerDetailHeroForPaddedContent(
  mobileFullWidth: boolean,
  hero: ReactNode | null | undefined,
) {
  if (!hero || !mobileFullWidth) {
    return hero ?? null
  }

  return (
    <DrawerMobileFullBleedGallery
      className="break-out-padding"
      data-testid="drawer-detail-mobile-hero"
    >
      {hero}
    </DrawerMobileFullBleedGallery>
  )
}

export function DrawerDetailBodyShell({
  leadingHero,
  children,
}: {
  leadingHero?: ReactNode
  children: ReactNode
}) {
  return (
    <Group direction="vertical" horizontalAlign="center">
      {leadingHero}
      {children}
    </Group>
  )
}
